import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../config';
import { useUser } from '../contexts/UserContext';
import Spinner from './Spinner';
import './MySubmissions.css';
import '../styles/ResizableTable.css';
import { initResizableTables } from '../utils/resizableTables';

interface PartnerRecord {
  id: number;
  initiative_id: number;
  initiative_name: string;
  direction: 'sent' | 'received';
  partner_email: string;
  message: string | null;
  owner_comment: string | null;
  status: string;
  created_at: string | null;
}

const statusClass: Record<string, string> = {
  active:    'status-badge status-pending',
  accepted:  'status-badge status-approved',
  withdrawn: 'status-badge status-rejected',
};
const statusText: Record<string, string> = {
  active: 'Pending', accepted: 'Accepted', withdrawn: 'Withdrawn',
};

const PartnershipRequests: React.FC = () => {
  const { user } = useUser();
  const [sent, setSent] = useState<PartnerRecord[]>([]);
  const [received, setReceived] = useState<PartnerRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'sent' | 'received'>('sent');

  useEffect(() => {
    if (!user?.email) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(api(`/api/initiatives/my/partnership-activity?user_email=${encodeURIComponent(user.email!)}`));
        setSent(res.data.sent || []);
        setReceived(res.data.received || []);
      } catch { /* silent */ }
      setIsLoading(false);
    };
    load();
  }, [user?.email]);

  useEffect(() => {
    if (!isLoading) {
      const t = setTimeout(() => initResizableTables(), 50);
      return () => clearTimeout(t);
    }
  }, [isLoading, activeSection]);

  const fmt = (iso: string | null) => {
    if (!iso) return 'â€”';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const rows = activeSection === 'sent' ? sent : received;

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        {(['sent', 'received'] as const).map(sec => (
          <button
            key={sec}
            onClick={() => setActiveSection(sec)}
            style={{
              padding: '6px 18px', fontSize: 13, borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              border: activeSection === sec ? '1px solid #c00' : '1px solid #d0d5dd',
              background: activeSection === sec ? '#c00' : '#fff',
              color: activeSection === sec ? '#fff' : '#344054',
            }}
          >
            {sec === 'sent' ? `Sent (${sent.length})` : `Received (${received.length})`}
          </button>
        ))}
      </div>

      {isLoading ? <Spinner /> : (
        <div className="submissions-table">
          <table id={`partnership-${activeSection}-table`}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Initiative</th>
                <th style={{ textAlign: 'left' }}>{activeSection === 'sent' ? 'Sent To (Owner)' : 'Requester'}</th>
                <th style={{ textAlign: 'left' }}>Your Message</th>
                <th style={{ textAlign: 'left' }}>Owner Response</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#aaa', fontSize: 13 }}>No records found.</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} className="submission-row">
                  <td className="name-cell" style={{ textAlign: 'left' }}>{r.initiative_name}</td>
                  <td style={{ textAlign: 'left', color: '#475467' }}>
                    {activeSection === 'sent' ? r.partner_email : r.partner_email}
                  </td>
                  <td style={{ textAlign: 'left', color: '#475467' }}>{r.message || <span style={{ color: '#ccc' }}>â€”</span>}</td>
                  <td style={{ textAlign: 'left', color: '#475467' }}>{r.owner_comment || <span style={{ color: '#ccc' }}>â€”</span>}</td>
                  <td>{fmt(r.created_at)}</td>
                  <td>
                    <span className={statusClass[r.status] || 'status-badge status-rejected'}>
                      {statusText[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PartnershipRequests;
